import { useCallback, useState } from "react";
import { UseEditQuizStateProps } from "./useEditQuizState";
import { dispatchProxyMessage } from "@verity/extension/proxy-message/external";

type UseThemeStateProps = Pick<UseEditQuizStateProps, "proctoringAssessmentConfiguration" | "quizInfo" | "quizType">;

/**Anything related to theme */
export default function useThemeState({ proctoringAssessmentConfiguration, quizInfo, quizType }: UseThemeStateProps) {
	/* Canvas Theme Disclaimer */
	const [themeExists, setThemeExists] = useState<boolean>(false);
	const [verifyingTheme, setVerifyingTheme] = useState<boolean>(false);
	/* Page can be called asynchronously, use state to determine if background steps have been completed */
	const [backgroundStepsComplete, setBackgroundStepsComplete] = useState<boolean>(false);

	const runCanvasThemeValidator = useCallback(() => {
		if (window.chrome) {
			chrome.runtime
				.sendMessage(import.meta.env.VITE_EXTENSION_ID, { request: "checkThemeExists" })
				.then((res) => {
					setThemeExists(res.status);
				})
				.catch((e) => {
					console.error(e);
					setThemeExists(false);
				});
		} else {
			dispatchProxyMessage({ request: "checkThemeExists" })
				.then((res: { status: boolean }) => {
					setThemeExists(res.status);
				})
				.catch((e) => {
					console.error(e);
					setThemeExists(false);
				});
		}

		return;
	}, []);

	const handleFetchAndSetTheme = useCallback(() => {
		if (backgroundStepsComplete) {
			return;
		}

		/* Handle according to LMS */
		if (quizInfo.lms === "canvas") {
			/* Page has not received configuration */
			if (!proctoringAssessmentConfiguration) {
				return;
			}

			/* Check if quiz is third party */
			if (!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption && quizType !== "standalone") {
				/* If not, verify if theme exists */
				setVerifyingTheme(true);
				runCanvasThemeValidator();
				setVerifyingTheme(false);
			}

			setBackgroundStepsComplete(true);
		} else {
			/* Implement other LMS background steps if required via additional conditions */
			setBackgroundStepsComplete(true);
		}
	}, [backgroundStepsComplete, proctoringAssessmentConfiguration, quizInfo.lms, quizType, runCanvasThemeValidator]);

	return {
		state: {
			themeExists,
			verifyingTheme,
		},
		action: {
			handleFetchAndSetTheme,
		},
	};
}
